// src/context/AppContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useLeave } from '../hooks/useLeave';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { user: currentUser, isAuthenticated, loading: authLoading } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [performanceTargets, setPerformanceTargets] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);

  const leaveHook = useLeave();

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {

      const employeesRes = await api.get('/api/employees/');
      const employeesList = Array.isArray(employeesRes) ? employeesRes :
        (employeesRes.results ? employeesRes.results : []);
      setEmployees(employeesList);

      try {
        const performanceRes = await api.get('/api/performance/targets/');
        const targetsList = Array.isArray(performanceRes) ? performanceRes :
          (performanceRes.results ? performanceRes.results : []);
        setPerformanceTargets(targetsList);
      } catch (perfErr) {
        ('Could not load performance targets:', perfErr.message);
        setPerformanceTargets([]);
      }

      try {
        const leaveRes = await api.get('/api/leave/my-requests/');
        const leaveList = Array.isArray(leaveRes) ? leaveRes :
          (leaveRes.results ? leaveRes.results : []);
        setLeaveRequests(leaveList);
      } catch (leaveErr) {
        ('Could not load leave requests:', leaveErr.message);
        setLeaveRequests([]);
      }

      setDataLoaded(true);

    } catch (err) {
      setError(err.message || 'Failed to load data');
      setDataLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ THE KEY FIX: react to isAuthenticated/authLoading from AuthContext,
  // not to a manual token read that only runs once on mount.
  useEffect(() => {
    if (authLoading) {
      // AuthContext hasn't resolved yet — wait
      return;
    }

    if (isAuthenticated) {
      loadAllData();
    } else {
      setEmployees([]);
      setPerformanceTargets([]);
      setLeaveRequests([]);
      setIsLoading(false);
      setDataLoaded(true);
    }
  }, [isAuthenticated, authLoading, refreshTrigger, loadAllData]);

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const value = useMemo(() => ({
    employees,
    performanceTargets,
    leaveRequests,
    isLoading,
    error,
    refreshData,
    dataLoaded,
    getDashboardStats: () => {
      const totalEmployees = employees.length;
      const activeEmployees = employees.filter(emp => emp.status === 'Active').length;

      const today = new Date().toISOString().split('T')[0];
      const onLeaveToday = leaveRequests.filter(leave =>
        leave.status === 'approved' &&
        leave.start_date <= today &&
        leave.end_date >= today
      ).length;

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const newHires = employees.filter(emp => {
        const joinDate = new Date(emp.joining_date);
        return joinDate.getMonth() === currentMonth && joinDate.getFullYear() === currentYear;
      }).length;

      const attendance = totalEmployees > 0
        ? Math.round(((totalEmployees - onLeaveToday) / totalEmployees) * 100)
        : 0;

      return {
        totalEmployees,
        activeEmployees,
        onLeave: onLeaveToday,
        newHires,
        attendance,
        monthlyGrowth: totalEmployees > 0 ? Math.round((newHires / totalEmployees) * 100) : 0
      };
    },
    getCurrentEmployee: () => {
      if (!currentUser) return null;
      return employees.find(emp => emp.employee_id === currentUser.employee_id);
    },
    getPerformanceTarget: (employeeId, month) => {
      return performanceTargets.find(t => t.employee_id === employeeId && t.month === month);
    },
    getRecentActivities: () => {
      const activities = [];
      leaveRequests.slice(0, 3).forEach(leave => {
        activities.push({
          id: `leave-${leave.id}`,
          title: `${leave.employee_name || 'Employee'} has applied for leave`,
          date: new Date(leave.applied_at || leave.start_date).toLocaleDateString(),
          description: leave.leave_type_display || leave.leave_type,
          type: 'leave'
        });
      });
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentHires = employees.filter(emp => {
        const joinDate = new Date(emp.joining_date);
        return joinDate >= thirtyDaysAgo;
      }).slice(0, 2);
      recentHires.forEach(emp => {
        activities.push({
          id: `hire-${emp.id}`,
          title: `${emp.user?.first_name || emp.name} has joined the company`,
          date: new Date(emp.joining_date).toLocaleDateString(),
          description: emp.position,
          type: 'hire'
        });
      });
      return activities.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
    },
    getUpcomingBirthdays: () => {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentDay = currentDate.getDate();
      return employees.filter(emp => {
        if (!emp.date_of_birth) return false;
        const birthDate = new Date(emp.date_of_birth);
        const birthMonth = birthDate.getMonth();
        const birthDay = birthDate.getDate();
        if (birthMonth === currentMonth && birthDay >= currentDay) return true;
        if (birthMonth === currentMonth + 1 && birthDay <= currentDay + 30) return true;
        return false;
      }).slice(0, 3);
    },
    leave: {
      getMyBalance: leaveHook.getMyBalance,
      getMySummary: leaveHook.getMySummary,
      getMyRequests: leaveHook.getMyRequests,
      applyLeave: leaveHook.applyLeave,
      updateLeave: leaveHook.updateLeave,
      submitDraft: leaveHook.submitDraft,
      cancelLeave: leaveHook.cancelLeave,
      withdrawLeave: leaveHook.withdrawLeave,
      deleteLeave: leaveHook.deleteLeave,
      getPendingRequests: leaveHook.getPendingRequests,
      getApprovedRequests: leaveHook.getApprovedRequests,
      getRejectedRequests: leaveHook.getRejectedRequests,
      getHrDashboard: leaveHook.getHrDashboard,
      approveLeave: leaveHook.approveLeave,
      rejectLeave: leaveHook.rejectLeave,
      reopenLeave: leaveHook.reopenLeave,
      getEmployeeLeaves: leaveHook.getEmployeeLeaves,
      adjustBalance: leaveHook.adjustBalance,
      bulkApprove: leaveHook.bulkApprove,
      bulkReject: leaveHook.bulkReject,
      getBalanceHistory: leaveHook.getBalanceHistory,
      loading: leaveHook.loading,
      error: leaveHook.error,
      clearError: leaveHook.clearError,
    },
  }), [employees, performanceTargets, leaveRequests, isLoading, error, dataLoaded, currentUser, leaveHook]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};